// =============================================================================
//
// FILE:
// src/modules/finance/reports/cash_flow/pages/CashFlowPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Cash Flow Statement
//
// PURPOSE:
// Cash Flow report with Direct / Indirect Method selection.
//
// DRILL-DOWN:
//
// Cash Flow
//     ↓
// Operating Activities
//     ↓
// Account
//     ↓
// Transaction / Journal
//
//     ↓
// Investing Activities
//
//     ↓
// Financing Activities
//
// IMPORTANT:
// - No accounting calculations in React
// - Backend remains authoritative
// - Direct / Indirect calculation is performed by backend
//
// =============================================================================

import React, {
    useState,
} from "react";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import cashFlowService
    from "../../../services/cashFlowService";

import financeLookupService
    from "../../../services/financeLookupService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function CashFlowPage() {

    // =========================================================================
    // STATE
    // =========================================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        reportData,
        setReportData,
    ] = useState(null);

    // =============================================================================
    // EXPANDED CASH FLOW ACCOUNTS
    // =============================================================================


    // =========================================================================
    // CASH FLOW EXPANSION STATE
    // =========================================================================

    const [
        expandedSections,
        setExpandedSections,
    ] = useState({
        operating: true,
        investing: true,
        financing: true,
    });


    const [
        expandedAccounts,
        setExpandedAccounts,
    ] = useState({});


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");


    // =========================================================================
    // LOOKUP STATE
    // =========================================================================

    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);


    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);


    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);


    // =========================================================================
    // CASH FLOW METHOD
    // =========================================================================

    const [
        cashFlowMethod,
        setCashFlowMethod,
    ] = useState("INDIRECT");


    // =========================================================================
    // FILTERS
    // =========================================================================

    const [
        filters,
        setFilters,
    ] = useState({

        company_id:
            null,

        financial_year_id:
            null,

        financial_period_id:
            null,

        cash_flow_method:
            "INDIRECT",

    });


    // =========================================================================
    // COMPANY LOOKUP
    // =========================================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name ||
                        company.name ||
                        `Company ${company.id}`,

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };


    // =========================================================================
    // FINANCIAL YEAR LOOKUP
    // =========================================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchFinancialYears(
                        inputValue
                    );

            return (
                response || []
            ).map(
                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };


    // =========================================================================
    // FINANCIAL PERIOD LOOKUP
    // =========================================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !filters.financial_year_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchFinancialPeriods(
                        filters.financial_year_id,
                        inputValue
                    );

            return [

                {
                    value:
                        null,

                    label:
                        "Full Financial Year",

                    data:
                        null,

                },

                ...(response || [])
                    .map(
                        period => ({

                            value:
                                period.id,

                            label:
                                period.period_name ||
                                period.period_code ||
                                `Period ${period.id}`,

                            data:
                                period,

                        })
                    ),

            ];

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };


    // =========================================================================
    // UPDATE FILTER
    // =========================================================================

    const updateFilter = (
        field,
        value
    ) => {

        setFilters(
            previous => ({

                ...previous,

                [field]:
                    value,

            })
        );

    };


    // =========================================================================
    // SEARCH
    // =========================================================================

    const handleSearch =
        async () => {

            setErrorMessage(
                ""
            );


            // -----------------------------------------------------------------
            // VALIDATION
            // -----------------------------------------------------------------

            if (
                !filters.company_id
            ) {

                setErrorMessage(
                    "Please select Company."
                );

                return;

            }


            if (
                !filters.financial_year_id
            ) {

                setErrorMessage(
                    "Please select Financial Year."
                );

                return;

            }


            setLoading(
                true
            );


            try {

                const response =
                    await cashFlowService
                        .getCashFlow(
                            filters
                        );


                setReportData(
                    response
                );


                setExpandedSections({
                    operating: true,
                    investing: true,
                    financing: true,
                });

                setExpandedAccounts({});

            } catch (error) {

                console.error(
                    "Failed to load Cash Flow:",
                    error
                );


                setReportData(
                    null
                );


                setErrorMessage(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to load Cash Flow Statement."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // =========================================================================
    // RESET
    // =========================================================================

    const handleReset =
        () => {

            setFilters({

                company_id:
                    null,

                financial_year_id:
                    null,

                financial_period_id:
                    null,

                cash_flow_method:
                    "INDIRECT",

            });


            setSelectedCompany(
                null
            );


            setSelectedFinancialYear(
                null
            );


            setSelectedFinancialPeriod(
                null
            );


            setCashFlowMethod(
                "INDIRECT"
            );


            setReportData(
                null
            );


            setExpandedSections({
                operating: true,
                investing: true,
                financing: true,
            });


            setExpandedAccounts(
                {}
            );


            setErrorMessage(
                ""
            );

        };


    // =========================================================================
    // FORMAT AMOUNT
    // =========================================================================

    const formatAmount =
        value => {

            const amount =
                Number(
                    value || 0
                );

            return amount.toLocaleString(
                "en-IN",
                {
                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2,
                }
            );

        };


    // =========================================================================
    // TOGGLE SECTION
    // =========================================================================

    const toggleSection =
        sectionKey => {

            setExpandedSections(
                previous => ({

                    ...previous,

                    [sectionKey]:
                        !previous[
                            sectionKey
                        ],

                })
            );

        };


    // =========================================================================
    // TOGGLE ACCOUNT
    // =========================================================================

    const toggleAccount =
        (
            sectionKey,
            accountId
        ) => {

            const key =
                `${sectionKey}:${accountId}`;


            setExpandedAccounts(
                previous => ({

                    ...previous,

                    [key]:
                        !previous[key],

                })
            );

        };


    // =========================================================================
    // ACCOUNT EXPANSION CHECK
    // =========================================================================

    const isAccountExpanded =
        (
            sectionKey,
            accountId
        ) => {

            return !!expandedAccounts[
                `${sectionKey}:${accountId}`
            ];

        };


    // =========================================================================
    // SUMMARY CARDS
    // =========================================================================

    const summaryCards =
        reportData
            ? [

                {
                    title:
                        "Opening Cash",

                    value:
                        formatAmount(
                            reportData
                                ?.cash_summary
                                ?.opening_cash
                        ),
                },

                {
                    title:
                        "Net Cash Change",

                    value:
                        formatAmount(
                            reportData
                                ?.cash_summary
                                ?.net_cash_change
                        ),
                },

                {
                    title:
                        "Closing Cash",

                    value:
                        formatAmount(
                            reportData
                                ?.cash_summary
                                ?.closing_cash ??
                            reportData
                                ?.cash_summary
                                ?.actual_closing_cash
                        ),
                },

                {
                    title:
                        "Reconciliation",

                    value:
                        reportData
                            ?.is_reconciled
                            ? "RECONCILED"
                            : "CHECK",

                },

            ]
            : [];


    // =========================================================================
    // TRANSACTION ROWS
    // =========================================================================

    const renderTransactions =
        (
            transactions = []
        ) => {

            if (
                !transactions.length
            ) {

                return (

                    <tr>

                        <td
                            colSpan="6"
                            className="text-center text-muted py-3"
                        >

                            No journal transactions found.

                        </td>

                    </tr>

                );

            }


            return transactions.map(
                (
                    transaction,
                    index
                ) => (

                    <tr
                        key={
                            transaction.journal_header_id ||
                            transaction.id ||
                            index
                        }
                    >

                        <td>

                            {
                                transaction.journal_date ||
                                "-"
                            }

                        </td>


                        <td>

                            <strong>
                                {
                                    transaction.journal_no ||
                                    "-"
                                }
                            </strong>

                        </td>


                        <td>

                            {
                                transaction.account_code ||
                                "-"
                            }

                            {" - "}

                            {
                                transaction.account_name ||
                                "-"
                            }

                        </td>


                        <td>

                            {
                                transaction.cash_account_code ||
                                "-"
                            }

                            {" - "}

                            {
                                transaction.cash_account_name ||
                                "-"
                            }

                        </td>


                        <td>

                            <span
                                className={
                                    transaction.direction ===
                                    "INFLOW"
                                        ? "badge bg-success"
                                        : "badge bg-danger"
                                }
                            >

                                {
                                    transaction.direction ||
                                    "-"
                                }

                            </span>

                        </td>


                        <td
                            className="text-end"
                        >

                            {
                                formatAmount(
                                    transaction.amount
                                )
                            }

                        </td>

                    </tr>

                )
            );

        };


    // =========================================================================
    // ACCOUNT ROWS
    // =========================================================================

    const renderAccountRows =
        (
            sectionKey,
            accounts = [],
            transactions = []
        ) => {

            if (
                !accounts.length
            ) {

                return (

                    <tr>

                        <td
                            colSpan="3"
                            className="text-center text-muted py-3"
                        >

                            No account transactions found.

                        </td>

                    </tr>

                );

            }


            return accounts.map(
                account => {

                    const accountId =
                        account.account_id;


                    const accountExpanded =
                        isAccountExpanded(
                            sectionKey,
                            accountId
                        );


                    const accountTransactions =
                        transactions.filter(
                            transaction =>
                                Number(
                                    transaction.account_id
                                ) === Number(
                                    accountId
                                )
                        );


                    return (

                        <React.Fragment
                            key={
                                `${sectionKey}-${accountId}`
                            }
                        >

                            <tr>

                                <td
                                    style={{
                                        paddingLeft:
                                            "32px",
                                    }}
                                >

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                                        onClick={() =>
                                            toggleAccount(
                                                sectionKey,
                                                accountId
                                            )
                                        }
                                        title={
                                            accountExpanded
                                                ? "Collapse account"
                                                : "Drill down to journals"
                                        }
                                    >

                                        <i
                                            className={
                                                accountExpanded
                                                    ? "fas fa-chevron-down"
                                                    : "fas fa-chevron-right"
                                            }
                                        />

                                    </button>

                                    <strong>

                                        {
                                            account.account_code
                                        }

                                    </strong>

                                </td>


                                <td>

                                    {
                                        account.account_name
                                    }

                                </td>


                                <td
                                    className="text-end"
                                >

                                    {
                                        formatAmount(
                                            account.amount
                                        )
                                    }

                                </td>

                            </tr>


                            {
                                accountExpanded && (

                                    <tr>

                                        <td
                                            colSpan="3"
                                            className="p-0"
                                        >

                                            <div
                                                className="bg-light border-top border-bottom"
                                                style={{
                                                    marginLeft:
                                                        "32px",
                                                }}
                                            >

                                                <div
                                                    className="px-3 py-2 border-bottom"
                                                >

                                                    <strong>
                                                        Journal Transactions
                                                    </strong>

                                                    <span
                                                        className="text-muted ms-2"
                                                    >

                                                        {
                                                            account.account_code
                                                        }

                                                        {" - "}

                                                        {
                                                            account.account_name
                                                        }

                                                    </span>

                                                </div>


                                                <div
                                                    className="table-responsive"
                                                >

                                                    <table
                                                        className="table table-sm table-hover mb-0"
                                                    >

                                                        <thead>

                                                            <tr>

                                                                <th>
                                                                    Date
                                                                </th>

                                                                <th>
                                                                    Journal No.
                                                                </th>

                                                                <th>
                                                                    Account
                                                                </th>

                                                                <th>
                                                                    Cash / Bank
                                                                </th>

                                                                <th>
                                                                    Direction
                                                                </th>

                                                                <th
                                                                    className="text-end"
                                                                >
                                                                    Amount
                                                                </th>

                                                            </tr>

                                                        </thead>


                                                        <tbody>

                                                            {
                                                                renderTransactions(
                                                                    accountTransactions
                                                                )
                                                            }

                                                        </tbody>

                                                    </table>

                                                </div>

                                            </div>

                                        </td>

                                    </tr>

                                )
                            }

                        </React.Fragment>

                    );

                }
            );

        };


    // =========================================================================
    // SECTION RENDERER
    // =========================================================================

    // =============================================================================
// SECTION RENDERER
//
// DIRECT METHOD
//
// Operating / Investing / Financing
//     ↓
// Account
//     ↓
// Journal Transactions
//
// INDIRECT METHOD - OPERATING
//
// Operating Activities
//     ↓
// Net Profit / Loss
//     ↓
// Non-Cash Adjustments
//     ↓
// Account
//     ↓
// Journal Transactions
//     ↓
// Working Capital Changes
//     ↓
// Account
//     ↓
// Journal Transactions
// =============================================================================

const renderSection =
    (
        sectionKey,
        title,
        section
    ) => {

        if (
            !section
        ) {

            return null;

        }


        const isExpanded =
            !!expandedSections[
                sectionKey
            ];


        // =================================================================
        // INDIRECT METHOD - OPERATING ACTIVITIES
        // =================================================================

        if (
            sectionKey === "operating" &&
            cashFlowMethod === "INDIRECT"
        ) {

            const nonCash =
                section
                    .non_cash_adjustments ||
                {
                    accounts:
                        [],
                    transactions:
                        [],
                    total:
                        0,
                };


            const workingCapital =
                section
                    .working_capital_changes ||
                {
                    accounts:
                        [],
                    transactions:
                        [],
                    total:
                        0,
                };


            return (

                <div
                    className="card mt-3"
                >

                    {/* =====================================================
                        OPERATING HEADER
                    ===================================================== */}

                    <div
                        className="card-header d-flex justify-content-between align-items-center"
                    >

                        <div
                            className="d-flex align-items-center"
                        >

                            <button
                                type="button"
                                className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                                onClick={() =>
                                    toggleSection(
                                        sectionKey
                                    )
                                }
                                title={
                                    isExpanded
                                        ? "Collapse section"
                                        : "Expand section"
                                }
                            >

                                <i
                                    className={
                                        isExpanded
                                            ? "fas fa-chevron-down"
                                            : "fas fa-chevron-right"
                                    }
                                />

                            </button>


                            <strong>
                                {title}
                            </strong>

                        </div>


                        <strong>

                            ₹{" "}

                            {
                                formatAmount(
                                    section.total
                                )
                            }

                        </strong>

                    </div>


                    {/* =====================================================
                        OPERATING CONTENT
                    ===================================================== */}

                    {
                        isExpanded && (

                            <div
                                className="card-body p-0"
                            >

                                {/* =========================================
                                    NET PROFIT / LOSS
                                ========================================= */}

                                <div
                                    className="px-3 py-3 border-bottom"
                                >

                                    <div
                                        className="d-flex justify-content-between align-items-center"
                                    >

                                        <strong>
                                            Net Profit / Loss
                                        </strong>


                                        <strong>

                                            ₹{" "}

                                            {
                                                formatAmount(
                                                    section
                                                        .profit_before_adjustments
                                                )
                                            }

                                        </strong>

                                    </div>

                                </div>


                                {/* =========================================
                                    NON-CASH ADJUSTMENTS
                                ========================================= */}

                                <div
                                    className="border-bottom"
                                >

                                    <div
                                        className="px-3 py-2 bg-light d-flex justify-content-between align-items-center"
                                    >

                                        <strong>
                                            Non-Cash Adjustments
                                        </strong>


                                        <strong>

                                            ₹{" "}

                                            {
                                                formatAmount(
                                                    nonCash.total
                                                )
                                            }

                                        </strong>

                                    </div>


                                    {
                                        nonCash.accounts &&
                                        nonCash.accounts.length > 0 ? (

                                            <div
                                                className="table-responsive"
                                            >

                                                <table
                                                    className="table table-hover table-bordered mb-0"
                                                >

                                                    <thead>

                                                        <tr>

                                                            <th
                                                                style={{
                                                                    width:
                                                                        "25%",
                                                                }}
                                                            >
                                                                Account Code
                                                            </th>

                                                            <th>
                                                                Account Name
                                                            </th>

                                                            <th
                                                                className="text-end"
                                                            >
                                                                Amount
                                                            </th>

                                                        </tr>

                                                    </thead>


                                                    <tbody>

                                                        {
                                                            renderAccountRows(
                                                                "indirect-non-cash",
                                                                nonCash.accounts,
                                                                nonCash.transactions
                                                            )
                                                        }

                                                    </tbody>

                                                </table>

                                            </div>

                                        ) : (

                                            <div
                                                className="text-center text-muted py-3"
                                            >

                                                No non-cash adjustments found.

                                            </div>

                                        )
                                    }

                                </div>


                                {/* =========================================
                                    WORKING CAPITAL CHANGES
                                ========================================= */}

                                <div
                                    className="border-bottom"
                                >

                                    <div
                                        className="px-3 py-2 bg-light d-flex justify-content-between align-items-center"
                                    >

                                        <strong>
                                            Changes in Working Capital
                                        </strong>


                                        <strong>

                                            ₹{" "}

                                            {
                                                formatAmount(
                                                    workingCapital.total
                                                )
                                            }

                                        </strong>

                                    </div>


                                    {
                                        workingCapital.accounts &&
                                        workingCapital.accounts.length > 0 ? (

                                            <div
                                                className="table-responsive"
                                            >

                                                <table
                                                    className="table table-hover table-bordered mb-0"
                                                >

                                                    <thead>

                                                        <tr>

                                                            <th
                                                                style={{
                                                                    width:
                                                                        "25%",
                                                                }}
                                                            >
                                                                Account Code
                                                            </th>

                                                            <th>
                                                                Account Name
                                                            </th>

                                                            <th
                                                                className="text-end"
                                                            >
                                                                Adjustment
                                                            </th>

                                                        </tr>

                                                    </thead>


                                                    <tbody>

                                                        {
                                                            renderAccountRows(
                                                                "indirect-working-capital",
                                                                workingCapital.accounts,
                                                                workingCapital.transactions
                                                            )
                                                        }

                                                    </tbody>

                                                </table>

                                            </div>

                                        ) : (

                                            <div
                                                className="text-center text-muted py-3"
                                            >

                                                No working capital changes found.

                                            </div>

                                        )
                                    }

                                </div>


                                {/* =========================================
                                    NET OPERATING CASH
                                ========================================= */}

                                <div
                                    className="px-3 py-3"
                                >

                                    <div
                                        className="d-flex justify-content-between align-items-center"
                                    >

                                        <strong>
                                            Net Cash from Operating Activities
                                        </strong>


                                        <strong>

                                            ₹{" "}

                                            {
                                                formatAmount(
                                                    section.total
                                                )
                                            }

                                        </strong>

                                    </div>

                                </div>

                            </div>

                        )
                    }

                </div>

            );

        }


        // =================================================================
        // DIRECT / INVESTING / FINANCING
        // =================================================================

        return (

            <div
                className="card mt-3"
            >

                {/* =========================================================
                    SECTION HEADER
                ========================================================= */}

                <div
                    className="card-header d-flex justify-content-between align-items-center"
                >

                    <div
                        className="d-flex align-items-center"
                    >

                        <button
                            type="button"
                            className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                            onClick={() =>
                                toggleSection(
                                    sectionKey
                                )
                            }
                            title={
                                isExpanded
                                    ? "Collapse section"
                                    : "Expand section"
                            }
                        >

                            <i
                                className={
                                    isExpanded
                                        ? "fas fa-chevron-down"
                                        : "fas fa-chevron-right"
                                }
                            />

                        </button>


                        <strong>
                            {title}
                        </strong>

                    </div>


                    <strong>

                        ₹{" "}

                        {
                            formatAmount(
                                section.total
                            )
                        }

                    </strong>

                </div>


                {/* =========================================================
                    SECTION CONTENT
                ========================================================= */}

                {
                    isExpanded && (

                        <div
                            className="card-body p-0"
                        >

                            <div
                                className="table-responsive"
                            >

                                <table
                                    className="table table-hover table-bordered mb-0"
                                >

                                    <thead>

                                        <tr>

                                            <th
                                                style={{
                                                    width:
                                                        "25%",
                                                }}
                                            >
                                                Account Code
                                            </th>

                                            <th>
                                                Account Name
                                            </th>

                                            <th
                                                className="text-end"
                                                style={{
                                                    width:
                                                        "20%",
                                                }}
                                            >
                                                Amount
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {
                                            renderAccountRows(
                                                sectionKey,
                                                section.accounts ||
                                                    [],
                                                section.transactions ||
                                                    []
                                            )
                                        }

                                    </tbody>


                                    <tfoot>

                                        <tr>

                                            <th
                                                colSpan="2"
                                                className="text-end"
                                            >

                                                Total {title}

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        section.total
                                                    )
                                                }

                                            </th>

                                        </tr>

                                    </tfoot>

                                </table>

                            </div>

                        </div>

                    )
                }

            </div>

        );

    };


    // =========================================================================
    // CASH RECONCILIATION
    // =========================================================================

    const renderCashReconciliation =
        () => {

            const summary =
                reportData
                    ?.cash_summary;


            if (
                !summary
            ) {

                return null;

            }


            return (

                <div
                    className="card mt-3 mb-4"
                >

                    <div
                        className="card-header"
                    >

                        <strong>
                            Cash Reconciliation
                        </strong>

                    </div>


                    <div
                        className="card-body"
                    >

                        <div
                            className="row mb-2"
                        >

                            <div
                                className="col-md-8 text-end"
                            >

                                Opening Cash

                            </div>


                            <div
                                className="col-md-4 text-end"
                            >

                                <strong>

                                    {
                                        formatAmount(
                                            summary
                                                .opening_cash
                                        )
                                    }

                                </strong>

                            </div>

                        </div>


                        <div
                            className="row mb-2"
                        >

                            <div
                                className="col-md-8 text-end"
                            >

                                Net Cash Change

                            </div>


                            <div
                                className="col-md-4 text-end"
                            >

                                <strong>

                                    {
                                        formatAmount(
                                            summary
                                                .net_cash_change
                                        )
                                    }

                                </strong>

                            </div>

                        </div>


                        <div
                            className="row mb-2"
                        >

                            <div
                                className="col-md-8 text-end"
                            >

                                Closing Cash

                            </div>


                            <div
                                className="col-md-4 text-end"
                            >

                                <strong>

                                    {
                                        formatAmount(
                                            summary
                                                .closing_cash ??
                                            summary
                                                .actual_closing_cash
                                        )
                                    }

                                </strong>

                            </div>

                        </div>


                        <hr />


                        <div
                            className="row"
                        >

                            <div
                                className="col-md-8 text-end"
                            >

                                <strong>
                                    Status
                                </strong>

                            </div>


                            <div
                                className="col-md-4 text-end"
                            >

                                <span
                                    className={
                                        reportData
                                            .is_reconciled
                                            ? "badge bg-success"
                                            : "badge bg-danger"
                                    }
                                >

                                    {
                                        reportData
                                            .is_reconciled
                                            ? "RECONCILED"
                                            : "CHECK"
                                    }

                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            );

        };


    // =========================================================================
    // RENDER
    // =========================================================================

    return (

        <div
            className="container-fluid"
        >

            {/* =================================================================
                HEADER
            ================================================================= */}

            <FinancePageHeader
                title="Cash Flow Statement"
                subtitle="Statement of Cash Flows"
            />


            {/* =================================================================
                FILTER PANEL
            ================================================================= */}

            <FinanceSearchPanel
                loading={
                    loading
                }
                onSearch={
                    handleSearch
                }
                onReset={
                    handleReset
                }
            >

                {/* =============================================================
                    COMPANY
                ============================================================= */}

                <div
                    className="col-lg-3 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Company

                        <span
                            className="text-danger"
                        >
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadCompanies
                        }
                        isClearable
                        value={
                            selectedCompany
                        }
                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );

                                setSelectedFinancialPeriod(
                                    null
                                );

                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );

                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* =============================================================
                    FINANCIAL YEAR
                ============================================================= */}

                <div
                    className="col-lg-3 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Financial Year

                        <span
                            className="text-danger"
                        >
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialYears
                        }
                        isClearable
                        value={
                            selectedFinancialYear
                        }
                        onChange={
                            option => {

                                setSelectedFinancialYear(
                                    option
                                );

                                setSelectedFinancialPeriod(
                                    null
                                );

                                updateFilter(
                                    "financial_year_id",
                                    option?.value ||
                                    null
                                );

                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* =============================================================
                    FINANCIAL PERIOD
                ============================================================= */}

                <div
                    className="col-lg-3 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Financial Period

                        <span
                            className="text-muted ms-1"
                        >
                            (Optional)
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialPeriods
                        }
                        isClearable
                        isDisabled={
                            !filters.financial_year_id
                        }
                        value={
                            selectedFinancialPeriod
                        }
                        onChange={
                            option => {

                                setSelectedFinancialPeriod(
                                    option
                                );

                                updateFilter(
                                    "financial_period_id",
                                    option?.value ||
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* =============================================================
                    CASH FLOW METHOD
                ============================================================= */}

                <div
                    className="col-lg-3 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Cash Flow Method

                    </label>


                    <select
                        className="form-select"
                        value={
                            cashFlowMethod
                        }
                        onChange={
                            event => {

                                const method =
                                    event.target.value;

                                setCashFlowMethod(
                                    method
                                );

                                updateFilter(
                                    "cash_flow_method",
                                    method
                                );

                                setReportData(
                                    null
                                );

                                setExpandedSections({
                                    operating: true,
                                    investing: true,
                                    financing: true,
                                });

                                setExpandedAccounts({});

                            }
                        }
                    >

                        <option value="INDIRECT">
                            Indirect Method
                        </option>

                        <option value="DIRECT">
                            Direct Method
                        </option>

                    </select>

                </div>

            </FinanceSearchPanel>


            {/* =================================================================
                ERROR
            ================================================================= */}

            {
                errorMessage && (

                    <div
                        className="alert alert-danger mt-3"
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* =================================================================
                SUMMARY CARDS
            ================================================================= */}

            {
                reportData && (

                    <FinanceSummaryCards
                        cards={
                            summaryCards
                        }
                    />

                )
            }


            {/* =================================================================
                METHOD INDICATOR
            ================================================================= */}

            {
                reportData && (

                    <div
                        className="card mt-3"
                    >

                        <div
                            className="card-body py-2"
                        >

                            <strong>
                                Cash Flow Method:
                            </strong>

                            <span
                                className="badge bg-primary ms-2"
                            >

                                {
                                    reportData.method ===
                                    "DIRECT"
                                        ? "Direct Method"
                                        : "Indirect Method"
                                }

                            </span>

                        </div>

                    </div>

                )
            }


            {/* =================================================================
                OPERATING
            ================================================================= */}

            {
                reportData &&
                renderSection(
                    "operating",
                    "Operating Activities",
                    reportData
                        .operating_activities
                )
            }


            {/* =================================================================
                INVESTING
            ================================================================= */}

            {
                reportData &&
                renderSection(
                    "investing",
                    "Investing Activities",
                    reportData
                        .investing_activities
                )
            }


            {/* =================================================================
                FINANCING
            ================================================================= */}

            {
                reportData &&
                renderSection(
                    "financing",
                    "Financing Activities",
                    reportData
                        .financing_activities
                )
            }


            {/* =================================================================
                RECONCILIATION
            ================================================================= */}

            {
                reportData &&
                renderCashReconciliation()
            }

        </div>

    );

}